import { createContext, useContext, useRef, useState, useCallback } from "react";
import { Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

const PinPromptContext = createContext(null);
export const usePinPrompt = () => useContext(PinPromptContext);

export function PinPromptProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [opts, setOpts] = useState({});
  const [pin, setPin] = useState("");
  const resolverRef = useRef(null);

  const requestPin = useCallback((options = {}) => {
    setOpts(options);
    setPin("");
    setOpen(true);
    return new Promise((resolve) => { resolverRef.current = resolve; });
  }, []);

  const settle = (value) => {
    setOpen(false);
    const r = resolverRef.current;
    resolverRef.current = null;
    if (r) r(value);
  };

  return (
    <PinPromptContext.Provider value={requestPin}>
      {children}
      <Dialog open={open} onOpenChange={(o) => { if (!o) settle(null); }}>
        <DialogContent data-testid="wallet-pin-dialog" className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <span className="w-9 h-9 rounded-full bg-pula-green/15 grid place-items-center text-pula-green"><Lock className="w-4 h-4" /></span>
              {opts.title || "Enter your Wallet PIN"}
            </DialogTitle>
            <DialogDescription>{opts.description || "Confirm this payment with your Wallet PIN."}</DialogDescription>
          </DialogHeader>
          <Input data-testid="wallet-pin-input" autoFocus type="password" inputMode="numeric" value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
            onKeyDown={(e) => e.key === "Enter" && (pin || opts.allowEmpty) && settle(pin)}
            placeholder="••••" className="text-center text-2xl tracking-[0.5em] h-14 font-heading" />
          <DialogFooter className="gap-2">
            <Button data-testid="wallet-pin-cancel" variant="outline" onClick={() => settle(null)} className="rounded-full">Cancel</Button>
            <Button data-testid="wallet-pin-confirm" onClick={() => settle(pin)} disabled={!pin && !opts.allowEmpty} className="rounded-full bg-pula-green hover:bg-pula-greenhover text-white">Confirm &amp; pay</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PinPromptContext.Provider>
  );
}
